import { useEffect, useState } from "react";
import { getUserCountry, hasDiscount } from "../../utils/utils";
import ReactMarkdown from "react-markdown";
import Button from "../Shared/Button";
import { useMutation } from "@tanstack/react-query";
import { queryClient } from "../../main";
import { getApiErrorMessage } from "../../utils/apiError";
import { toast } from "react-toastify";
import { AddPackageToCart } from "../../api/Cart/AddPackageToCart";
import { useNavigate } from "react-router-dom";
import { useUserDataStore } from "../../stores/user";
import { getAccessToken } from "../../lib/session";

interface PackageCardProps {
    packages: {
        id: string;
        name: string;
        img_url: string;
        description: string;
        discountPercentage: number;
        actualPrice_egp: string;
        actualPrice_usd: string;
        discountPrice_usd: string;
        discountPrice_egp: string;
        courses: {
            id: string;
            name: string;
            img_url: string;
            actualPrice_egp: string;
            actualPrice_usd: string;
        }[];
    };
}

const PackageCard = ({ packages }: PackageCardProps) => {
    const navigate = useNavigate()
    const { userData } = useUserDataStore();
    const [country, setCountry] = useState<string | null>(null);
    const { mutate } = useMutation({
        mutationFn: () => AddPackageToCart(packages.id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["cart"] });
        },
        onError: (error: unknown) => {
            toast.error(getApiErrorMessage(error, "Error Adding To Cart"));
        },
    });

    useEffect(() => {
        const userCountry = getUserCountry();
        setCountry(userCountry);
    }, []);
    const handleBuyNowClick = () => {
        const accessToken = getAccessToken();
        if (accessToken) {
            mutate();
        } else {
            navigate("/login?cart=true");
        }
    };

    return (
        <div className="relative flex flex-col justify-between w-full h-full p-4 bg-white border rounded-2xl border-black/20">
            <div className="relative w-full mb-4">

                <img
                    src={packages.img_url}
                    className='object-cover h-[225px] w-full rounded-lg'
                />

                {
                    <>
                        {country === 'EG' ? (
                            <>
                                {hasDiscount(packages.actualPrice_egp, packages.discountPrice_egp) && (
                                    <div className="absolute px-2 border-red-700 h-7 border text-red-500 line-through bg-white bottom-[26px] font-semibold right-px rounded-tr rounded-br flex justify-center items-center md:text-base text-sm">
                                        {packages.actualPrice_egp} EGP
                                    </div>
                                )}
                            </>
                        ) : (
                            <>
                                {hasDiscount(packages.actualPrice_usd, packages.discountPrice_usd) && (
                                    <div className="absolute px-2 border-red-700 h-7 border text-red-500 line-through bg-white bottom-[26px] font-semibold right-px rounded-tr rounded-br flex justify-center items-center md:text-base text-sm">
                                        {packages.actualPrice_usd} USD
                                    </div>
                                )}
                            </>
                        )}
                    </>
                }

            </div>

            <div>
                <div className="flex justify-between w-full items-center mb-3 py-0.5">
                    <h1 className="text-lg font-semibold md:text-2xl sm:text-xl">{packages.name}</h1>

                    <div className="rounded py-1.5 min-w-[90px] px-3 flex justify-center items-center gap-0.5 font-bold bg-primary text-white">
                        {country === "EG" ? (
                            <>
                                {hasDiscount(packages.actualPrice_egp, packages.discountPrice_egp)
                                    ? packages.discountPrice_egp
                                    : packages.actualPrice_egp}{" "}
                                <span className="text-sm uppercase">EGP</span>
                            </>
                        ) : (
                            <>
                                {hasDiscount(packages.actualPrice_usd, packages.discountPrice_usd)
                                    ? packages.discountPrice_usd
                                    : packages.actualPrice_usd}{" "}
                                <span className="text-sm uppercase">USD</span>
                            </>
                        )}
                    </div>
                </div>
                <div className="mb-4 ">
                    <span className="text-xs font-medium sm:text-sm text-lightGray/70">
                        <ReactMarkdown>{packages.description}</ReactMarkdown>
                    </span>
                </div>
                <Button
                    variant="gray"
                    className="md:h-9 h-8 !rounded-lg font-semibold text-xs w-full"
                    onClick={handleBuyNowClick}
                    disabled={userData.role === "sales"}
                >
                    Add To Cart
                </Button>

            </div>
        </div>
    );
};

export default PackageCard;
